import type { APIConstructorProps, IHttpClient } from './Types';
import { AllEntities } from './Types';
import { DEFAULT_ENDPOINT_MAP } from './Defaults';
import {
    Auth,
    BaseEntityModuleFuncs,
    Buyer,
    Campaign,
    Campaign_Chat,
    CampaignReporting,
    CampaignInstruction,
    CampaignPopShot,
    Creative,
    CreativeGroup,
    Displays_AnalogueScreen,
    Displays_AudienceUpload,
    Displays_Owner,
    Displays_Pack,
    Displays_Screen,
    Displays_Tag,
    LineItem,
    Notifications,
    PopDisplays,
    PopDisplays_LogFileImportSummary,
    Reports,
    ReportsElasticSearch,
    Usage,
    Role,
    Schedule,
    Skin,
    User as UserModule,
    VirtualCampaignBuilder,
} from './Modules';
import HttpClient from './HttpClient';
import type ApiEntityEndpoints from './Types/ApiEntityEndpoints';

type EntitiesType = Omit<
    ApiEntityEndpoints,
    | 'Campaign'
    | 'Buyer'
    | 'CampaignInstruction'
    | 'CampaignPopShot'
    | 'Creative'
    | 'CreativeGroup'
    | 'User'
    | 'Schedule'
    | 'Skin'
    | 'CampaignReporting'
    | 'Role'
    | 'Displays_AnalogueScreen'
    | 'Displays_AudienceUpload'
    | 'Displays_LineItem'
    | 'Displays_Pack'
    | 'Displays_Screen'
    | 'Displays_Tag'
    | 'Displays_Owner'
    | 'Campaign_Chat'
    | 'PopDisplays_LogFileImportSummary'
> & {
    Buyer: ReturnType<typeof Buyer>;
    Campaign: ReturnType<typeof Campaign>;
    CampaignInstruction: ReturnType<typeof CampaignInstruction>;
    CampaignPopShot: ReturnType<typeof CampaignPopShot>;
    Creative: ReturnType<typeof Creative>;
    CreativeGroup: ReturnType<typeof CreativeGroup>;
    Displays_AnalogueScreen: ReturnType<typeof Displays_AnalogueScreen>;
    Displays_AudienceUpload: ReturnType<typeof Displays_AudienceUpload>;
    Displays_LineItem: ReturnType<typeof LineItem>;
    Displays_Pack: ReturnType<typeof Displays_Pack>;
    Displays_Screen: ReturnType<typeof Displays_Screen>;
    Displays_Tag: ReturnType<typeof Displays_Tag>;
    Displays_Owner: ReturnType<typeof Displays_Owner>;
    Campaign_Chat: ReturnType<typeof Campaign_Chat>;
    PopDisplays_LogFileImportSummary: ReturnType<typeof PopDisplays_LogFileImportSummary>;

    User: ReturnType<typeof UserModule>;
    Schedule: ReturnType<typeof Schedule>;
    Skin: ReturnType<typeof Skin>;
    CampaignReporting: ReturnType<typeof CampaignReporting>;
    Role: ReturnType<typeof Role>;
};

class API {
    private readonly httpClient: IHttpClient;

    private entities: EntitiesType;

    private auth: ReturnType<typeof Auth>;

    private reports: ReturnType<typeof Reports>;

    private reportsElasticSearch: ReturnType<typeof ReportsElasticSearch>;

    private notifications: ReturnType<typeof Notifications>;

    private usage: ReturnType<typeof Usage>;

    private virtualCampaignBuilder: ReturnType<typeof VirtualCampaignBuilder>;

    private popDisplays: ReturnType<typeof PopDisplays>;

    constructor({ baseUrl, endpoints = DEFAULT_ENDPOINT_MAP, httpClient }: APIConstructorProps) {
        this.httpClient = httpClient ?? new HttpClient(baseUrl);
        const di = { httpClient: this.httpClient, endpoints, baseUrl };

        const genericEndpointModules = Object.keys(new AllEntities()).reduce(
            (a, k) => ({ ...a, [k]: BaseEntityModuleFuncs(di, k as keyof AllEntities) }),
            {},
        ) as EntitiesType;

        this.entities = {
            ...genericEndpointModules,
            Buyer: Buyer(di),
            Campaign: Campaign(di),
            CampaignInstruction: CampaignInstruction(di),
            CampaignPopShot: CampaignPopShot(di),
            Creative: Creative(di),
            CreativeGroup: CreativeGroup(di),
            Displays_LineItem: LineItem(di),
            Role: Role(di),
            Schedule: Schedule(di),
            Skin: Skin(di),
            CampaignReporting: CampaignReporting(di),
            User: UserModule(di),
            Displays_AnalogueScreen: Displays_AnalogueScreen(di),
            Displays_AudienceUpload: Displays_AudienceUpload(di),
            Displays_Pack: Displays_Pack(di),
            Displays_Screen: Displays_Screen(di),
            Displays_Tag: Displays_Tag(di),
            Displays_Owner: Displays_Owner(di),
            Campaign_Chat: Campaign_Chat(di),
            PopDisplays_LogFileImportSummary: PopDisplays_LogFileImportSummary(di),
        };
        this.auth = Auth(di);
        this.reports = Reports(di);
        this.reportsElasticSearch = ReportsElasticSearch(di);
        this.notifications = Notifications(di);
        this.usage = Usage(di);
        this.virtualCampaignBuilder = VirtualCampaignBuilder(di);
        this.popDisplays = PopDisplays(di);
    }

    get HttpClient() {
        return this.httpClient;
    }

    get Entities() {
        return this.entities;
    }

    get Auth() {
        return this.auth;
    }

    get Reports() {
        return this.reports;
    }

    get ReportsElasticSearch() {
        return this.reportsElasticSearch;
    }

    get Notifications() {
        return this.notifications;
    }

    get Usage() {
        return this.usage;
    }

    get VirtualCampaignBuilder() {
        return this.virtualCampaignBuilder;
    }

    get PopDisplays() {
        return this.popDisplays;
    }
}

export default API;
